import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import InfoIcon from '@mui/icons-material/Info';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import NavBar from '../components/common/NavBar';
import PlatformDetailsDialog from '../components/common/PlatformDetailsDialog';
import {
  ClassroomOverviewMock,
  ExamPlanningCalendarMock,
  HeroDashboardPreview,
  InsightsClusterMock,
  WorksheetGradingMock,
} from '../components/landing/LandingVisualMocks';

const pageBg = '#f4f4fb';
const navy = '#0f172a';
const muted = '#64748b';

const steps = [
  { title: 'Set the focus', description: 'Subject, topic, or exam goal—what matters most right now.', icon: MenuBookRoundedIcon },
  { title: 'Assign & practise', description: 'Teachers assign fast; students practise with clear next steps.', icon: AssignmentTurnedInOutlinedIcon },
  { title: 'Worksheets & marking', description: 'Digital worksheets and quicker review for teachers and students.', icon: InsightsRoundedIcon },
  { title: 'Stay aligned', description: 'Parents see progress; everyone shares the same picture.', icon: WorkspacePremiumRoundedIcon },
];

const heroRoleChips = [
  { label: 'Students', value: 'Clear next steps', accent: '#4f46e5' },
  { label: 'Teachers', value: 'Less prep time', accent: '#0d9488' },
  { label: 'Parents', value: 'Real visibility', accent: '#c2410c' },
];

/** Landing testimonial carousel — same card layout, role-specific benefits */
const testimonialSlides = [
  {
    initial: 'S',
    name: 'Sofia',
    role: 'Math tutor',
    quote:
      'I can build tests in minutes, review worksheets without the paper chase, and show parents exactly where a student is improving—all from one place.',
    avatarBg: '#dbeafe',
    avatarColor: '#1d4ed8',
  },
  {
    initial: 'E',
    name: 'Emma',
    role: 'Student',
    quote:
      'I always know what to do next—weak topics, streaks, and scores sit in one dashboard—so revision before exams feels focused instead of overwhelming.',
    avatarBg: '#e0e7ff',
    avatarColor: '#4338ca',
  },
  {
    initial: 'P',
    name: 'Priya',
    role: 'Parent',
    quote:
      'I can see where my child is improving and which topics still need support without chasing teachers or piecing together emails—it keeps our weekly check-ins honest and calm.',
    avatarBg: '#fce7f3',
    avatarColor: '#be185d',
  },
];

const primaryButtonSx = {
  px: 3.5,
  py: 1.5,
  borderRadius: 999,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff',
  background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 55%, #7c3aed 100%)',
  boxShadow: '0 20px 44px rgba(37, 99, 235, 0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 55%, #6d28d9 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 24px 52px rgba(79, 70, 229, 0.38)',
  },
};

const outlineHeroSecondarySx = {
  px: 3.5,
  py: 1.5,
  borderRadius: 999,
  fontSize: '1rem',
  fontWeight: 700,
  borderColor: alpha(navy, 0.14),
  color: navy,
  bgcolor: alpha('#fff', 0.85),
  backdropFilter: 'blur(8px)',
  '&:hover': {
    borderColor: alpha(navy, 0.22),
    bgcolor: '#fff',
    transform: 'translateY(-2px)',
  },
};

const storyCardSx = {
  borderRadius: 4,
  border: '1px solid rgba(148, 163, 184, 0.16)',
  bgcolor: '#ffffff',
  boxShadow: '0 24px 56px rgba(15, 23, 42, 0.06)',
  transition: 'transform 200ms ease, box-shadow 200ms ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 28px 64px rgba(15, 23, 42, 0.1)',
  },
};

function SectionEyebrow({ children, light = false }) {
  return (
    <Typography
      sx={{
        color: light ? alpha('#fff', 0.72) : '#6366f1',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

export default function LandingPage() {
  const theme = useTheme();
  const [platformDetailsOpen, setPlatformDetailsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [registrationMessage, setRegistrationMessage] = useState(null);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialPaused, setTestimonialPaused] = useState(false);

  const testimonialCount = testimonialSlides.length;
  const goTestimonialPrev = useCallback(() => {
    setTestimonialIndex((i) => (i - 1 + testimonialCount) % testimonialCount);
  }, [testimonialCount]);
  const goTestimonialNext = useCallback(() => {
    setTestimonialIndex((i) => (i + 1) % testimonialCount);
  }, [testimonialCount]);

  useEffect(() => {
    if (testimonialPaused || testimonialCount < 2) return undefined;
    const id = setInterval(() => {
      setTestimonialIndex((i) => (i + 1) % testimonialCount);
    }, 9000);
    return () => clearInterval(id);
  }, [testimonialPaused, testimonialCount]);

  useEffect(() => {
    if (location.state?.registrationSuccess && location.state?.message) {
      setRegistrationMessage(location.state.message);
      setShowRegistrationSuccess(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.registrationSuccess, location.state?.message, location.pathname, navigate]);

  const handleCloseRegistrationSnackbar = () => setShowRegistrationSuccess(false);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: pageBg, color: navy }}>
      <Snackbar
        open={showRegistrationSuccess}
        autoHideDuration={12000}
        onClose={handleCloseRegistrationSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 20 }}
      >
        <Alert onClose={handleCloseRegistrationSnackbar} severity="success" variant="filled" sx={{ width: '100%' }}>
          {registrationMessage ?? 'Registration successful! Check your email to verify.'}
        </Alert>
      </Snackbar>

      <Box sx={{ position: 'sticky', top: 0, zIndex: 12 }}>
        <NavBar />
      </Box>

      <Box
        component="main"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -160,
            right: -100,
            width: 420,
            height: 420,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(129,140,248,0.22) 0%, rgba(129,140,248,0) 70%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 280,
            left: -140,
            width: 360,
            height: 360,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, rgba(167,139,250,0) 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* —— Hero —— */}
        <Box sx={{ position: 'relative', zIndex: 1, pt: { xs: 5, md: 7 }, pb: { xs: 8, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={3} sx={{ maxWidth: 600 }}>
                  <Chip
                    label="Education reimagined"
                    sx={{
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      px: 0.5,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      fontSize: '0.7rem',
                      bgcolor: '#ede9fe',
                      color: '#5b21b6',
                      border: '1px solid rgba(124,58,237,0.2)',
                    }}
                  />
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: '2.35rem', sm: '2.75rem', md: '3.35rem' },
                      fontWeight: 800,
                      lineHeight: 1.08,
                      letterSpacing: '-0.03em',
                      color: '#0c1222',
                    }}
                  >
                    Improve in maths with focused, personalised practice
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' }, lineHeight: 1.75, color: alpha(navy, 0.72), maxWidth: 540 }}>
                    Give students clearer next steps, help teachers prepare in minutes, and give parents real visibility into progress.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      component={Link}
                      to="/get-started"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowOutwardIcon />}
                      sx={primaryButtonSx}
                    >
                      Start Free Trial
                    </Button>
                    <Button component={Link} to="/login" variant="outlined" size="large" sx={outlineHeroSecondarySx}>
                      Login
                    </Button>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
                    <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                      {heroRoleChips.map((item) => (
                        <Chip
                          key={item.label}
                          label={`${item.label}: ${item.value}`}
                          sx={{
                            bgcolor: alpha(item.accent, 0.09),
                            color: '#334155',
                            border: `1px solid ${alpha(item.accent, 0.2)}`,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                    <Tooltip title="View platform coverage details" arrow>
                      <IconButton
                        onClick={() => setPlatformDetailsOpen(true)}
                        aria-label="View platform coverage details"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          border: '1px solid rgba(99, 102, 241, 0.14)',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14), transform: 'translateY(-1px)' },
                        }}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <HeroDashboardPreview />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* —— Adaptive exam planning —— */}
        <Box
          sx={{
            py: { xs: 8, md: 10 },
            position: 'relative',
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(237,233,254,0.65) 0%, rgba(244,244,251,0.9) 100%)',
          }}
        >
          <Container maxWidth="lg">
            <Card sx={{ ...storyCardSx, borderRadius: 5, p: { xs: 1, md: 0.5 } }}>
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <SectionEyebrow>Exam preparation</SectionEyebrow>
                    <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '2.25rem' }, fontWeight: 800, mb: 2, lineHeight: 1.15 }}>
                      Adaptive exam planning
                    </Typography>
                    <Typography sx={{ color: muted, lineHeight: 1.75, mb: 3, fontSize: '1.02rem' }}>
                      A personalised study plan that evolves with your progress—keeping preparation structured, focused, and effective.
                    </Typography>
                    <Stack spacing={1.5}>
                      {[
                        'Automatically adjusts as topics are completed',
                        'Prioritises weak areas based on performance',
                        'Keeps students focused with one clear next step',
                      ].map((t) => (
                        <Stack key={t} direction="row" spacing={1.25} alignItems="flex-start">
                          <CheckCircleIcon sx={{ fontSize: 22, color: '#16a34a', mt: 0.15 }} />
                          <Typography sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.6 }}>{t}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ExamPlanningCalendarMock />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Container>
        </Box>

        {/* —— Insights —— */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: pageBg, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" direction="row-reverse">
              <Grid item xs={12} md={6}>
                <SectionEyebrow>Performance</SectionEyebrow>
                <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '2.25rem' }, fontWeight: 800, mb: 2, lineHeight: 1.15 }}>
                  Deep insights into learning progress
                </Typography>
                <Typography sx={{ color: muted, lineHeight: 1.75, mb: 3, fontSize: '1.02rem' }}>
                  Accuracy, topic mastery, and weak-area signals—so students know what to do next, teachers see class patterns, and parents understand improvement.
                </Typography>
                <Button
                  component={Link}
                  to="/get-started"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.2,
                    fontWeight: 700,
                    borderColor: alpha('#7c3aed', 0.35),
                    color: '#5b21b6',
                    bgcolor: alpha('#7c3aed', 0.06),
                    '&:hover': { borderColor: '#7c3aed', bgcolor: alpha('#7c3aed', 0.1) },
                  }}
                >
                  Explore plans
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <InsightsClusterMock />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* —— Teachers: classroom overview —— */}
        <Box
          sx={{
            py: { xs: 8, md: 10 },
            position: 'relative',
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(244,244,251,0.98) 45%, #f4f4fb 100%)',
            borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 720, mb: { xs: 3.5, md: 4.5 } }}>
              <SectionEyebrow>Teacher and Parents</SectionEyebrow>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '2.25rem' }, fontWeight: 800, mb: 2, lineHeight: 1.15, color: '#1e1b4b' }}>
                Classroom Overview
              </Typography>
              <Typography sx={{ color: muted, lineHeight: 1.75, fontSize: '1.02rem' }}>
                Empower educators and parents with a high-level command center. Monitor real-time student progress, intervention needs, and curriculum mastery at a glance.
              </Typography>
            </Box>
            <ClassroomOverviewMock />
          </Container>
        </Box>

        {/* —— Worksheets —— */}
        <Box
          sx={{
            py: { xs: 9, md: 11 },
            position: 'relative',
            zIndex: 1,
            background: 'linear-gradient(165deg, #0f172a 0%, #1e1b4b 42%, #312e81 100%)',
            color: '#f8fafc',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.5,
              background:
                'radial-gradient(ellipse at 20% 0%, rgba(99,102,241,0.25) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(34,211,238,0.12) 0%, transparent 45%)',
              pointerEvents: 'none',
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative' }}>
            <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 5 }}>
              <SectionEyebrow light>Worksheets</SectionEyebrow>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '2.35rem' }, fontWeight: 800, mb: 2, lineHeight: 1.15 }}>
                Spend less time managing worksheets and progress tracking, more time teaching
              </Typography>
              <Typography sx={{ color: alpha('#f8fafc', 0.7), lineHeight: 1.75, fontSize: '1.05rem' }}>
                Upload, review, and grade in a focused flow—students get faster feedback; teachers reclaim hours each week.
              </Typography>
            </Box>
            <WorksheetGradingMock />
          </Container>
        </Box>

        {/* —— How it works —— */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: pageBg, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 5 }}>
              <SectionEyebrow>How it works</SectionEyebrow>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '2.2rem' }, fontWeight: 800, mb: 1.5 }}>
                From practice to progress—without the noise
              </Typography>
              <Typography sx={{ color: muted, lineHeight: 1.75 }}>A simple loop for students, teachers, and families.</Typography>
            </Box>
            <Grid container spacing={2.5}>
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={step.title}>
                    <Card sx={{ ...storyCardSx, height: '100%', borderRadius: 3 }}>
                      <CardContent sx={{ p: 2.75 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 2.5,
                                bgcolor: '#eef2ff',
                                color: '#4338ca',
                              }}
                            >
                              <Icon />
                            </Box>
                            <Typography sx={{ color: alpha(navy, 0.2), fontWeight: 800, fontSize: '1.25rem' }}>{String(index + 1).padStart(2, '0')}</Typography>
                          </Stack>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                            {step.title}
                          </Typography>
                          <Typography sx={{ color: muted, lineHeight: 1.65, fontSize: '0.92rem' }}>{step.description}</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* —— Testimonials carousel —— */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff', position: 'relative', zIndex: 1 }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <SectionEyebrow>Stories</SectionEyebrow>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: '#0c1222' }}>
                Trusted by tutors, students, and families
              </Typography>
            </Box>
            <Card
              component="section"
              aria-roledescription="carousel"
              aria-label="Customer testimonials"
              onMouseEnter={() => setTestimonialPaused(true)}
              onMouseLeave={() => setTestimonialPaused(false)}
              sx={{
                borderRadius: 5,
                border: '1px solid rgba(124, 58, 237, 0.12)',
                background: 'linear-gradient(135deg, rgba(245,243,255,0.9) 0%, rgba(255,255,255,1) 55%)',
                boxShadow: '0 28px 70px rgba(15, 23, 42, 0.07)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <FormatQuoteRoundedIcon
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 24,
                  fontSize: 120,
                  color: alpha('#7c3aed', 0.08),
                  pointerEvents: 'none',
                }}
              />
              <Stack direction="row" alignItems="center" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                <IconButton
                  size="small"
                  onClick={goTestimonialPrev}
                  aria-label="Previous testimonial"
                  sx={{ color: alpha(navy, 0.45), '&:hover': { color: navy, bgcolor: alpha(navy, 0.06) } }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={goTestimonialNext}
                  aria-label="Next testimonial"
                  sx={{ color: alpha(navy, 0.45), '&:hover': { color: navy, bgcolor: alpha(navy, 0.06) } }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <CardContent sx={{ p: { xs: 3, md: 5 }, pt: { xs: 5, sm: 5 } }}>
                <Box sx={{ minHeight: { xs: 300, sm: 260, md: 220 } }} aria-live="polite">
                  <Fade in timeout={380} key={testimonialIndex}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={3} md={2} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                        <Avatar
                          sx={{
                            width: 88,
                            height: 88,
                            bgcolor: testimonialSlides[testimonialIndex].avatarBg,
                            color: testimonialSlides[testimonialIndex].avatarColor,
                            fontSize: '1.75rem',
                            fontWeight: 800,
                          }}
                          aria-hidden
                        >
                          {testimonialSlides[testimonialIndex].initial}
                        </Avatar>
                      </Grid>
                      <Grid item xs={12} sm={9} md={10}>
                        <Typography sx={{ fontSize: { xs: '1.08rem', md: '1.28rem' }, lineHeight: 1.75, color: '#1e293b', fontWeight: 500, mb: 2 }}>
                          &ldquo;{testimonialSlides[testimonialIndex].quote}&rdquo;
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: navy }}>{testimonialSlides[testimonialIndex].name}</Typography>
                        <Typography sx={{ color: muted, fontSize: '0.95rem' }}>{testimonialSlides[testimonialIndex].role}</Typography>
                      </Grid>
                    </Grid>
                  </Fade>
                </Box>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                  {testimonialSlides.map((slide, i) => (
                    <IconButton
                      key={slide.name}
                      size="small"
                      onClick={() => setTestimonialIndex(i)}
                      aria-label={`Show testimonial from ${slide.name}`}
                      aria-current={i === testimonialIndex ? 'true' : undefined}
                      sx={{
                        p: 0.5,
                        color: i === testimonialIndex ? '#7c3aed' : alpha(navy, 0.22),
                        '&:hover': { color: '#7c3aed', bgcolor: alpha('#7c3aed', 0.08) },
                      }}
                    >
                      <Box
                        sx={{
                          width: i === testimonialIndex ? 22 : 8,
                          height: 8,
                          borderRadius: 999,
                          bgcolor: 'currentColor',
                          transition: 'width 200ms ease',
                        }}
                      />
                    </IconButton>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Container>
        </Box>

        {/* —— Final CTA —— */}
        <Box
          sx={{
            py: { xs: 9, md: 11 },
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #ede9fe 100%)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.85rem', md: '2.5rem' }, fontWeight: 800, mb: 2, color: '#0c1222', lineHeight: 1.2 }}>
              Ready for a smarter way to prepare for exams?
            </Typography>
            <Typography sx={{ color: muted, mb: 4, lineHeight: 1.75, fontSize: '1.05rem' }}>
              Start free, invite your students or family, and see progress become easier to act on.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/register" variant="contained" size="large" sx={primaryButtonSx}>
                Start Free Trial
              </Button>
              <Button component={Link} to="/login" variant="outlined" size="large" sx={outlineHeroSecondarySx}>
                Login
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* —— Footer —— */}
        <Box
          component="footer"
          sx={{
            py: 2.5,
            px: 2,
            bgcolor: '#f8f9fa',
            borderTop: '1px solid #e5e7eb',
            textAlign: 'center',
          }}
        >
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: '0.8125rem',
              color: '#6b7280',
              fontWeight: 400,
              letterSpacing: '0.01em',
            }}
          >
            {new Date().getFullYear()} ThinkStyne. All rights reserved.
          </Typography>
        </Box>
      </Box>

      <PlatformDetailsDialog open={platformDetailsOpen} onClose={() => setPlatformDetailsOpen(false)} />
    </Box>
  );
}
